import axios from '@axios'

const BASE_PATH = 'v1/policy-exceptions'

function buildUserOptionLabel(user) {
  const parts = [user.name, user.user, user.email].filter(Boolean)
  return parts.join(' | ')
}

function resolveTenantFromPolicyException(item, rootGetters) {
  if (!item) return null
  const units = item.units || []
  const firstUnit = Array.isArray(units) ? units[0] : null
  if (firstUnit && firstUnit.router && firstUnit.router.id_tenant != null) {
    return firstUnit.router.id_tenant
  }
  try {
    const tenants = rootGetters['user/getTenants'] || rootGetters['tenant/getSelectedItems'] || []
    const first = Array.isArray(tenants) ? tenants[0] : null
    if (first && (first.id != null || first.value != null)) return first.id ?? first.value
  } catch (_) {}
  return null
}

function extractPolicyExceptionFromResponse(response) {
  if (!response || typeof response !== 'object') return null
  const candidate = response.data != null ? response.data : (response.policy_exception != null ? response.policy_exception : response)
  if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
    const inner = candidate.data != null ? candidate.data : candidate.policy_exception
    if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
      const innerData = inner.data != null ? inner.data : inner.policy_exception
      if (innerData && typeof innerData === 'object' && !Array.isArray(innerData)) {
        return innerData
      }
      return inner
    }
    if (candidate.data && typeof candidate.data === 'object' && !Array.isArray(candidate.data)) {
      return candidate.data
    }
    if (candidate.policy_exception && typeof candidate.policy_exception === 'object') {
      return candidate.policy_exception
    }
    return candidate
  }
  return null
}

export default {
  namespaced: true,
  state: {
    items: [],
    current: null,
    catalogs: {
      scope_types: [],
      request_types: [],
      exception_types: [],
      statuses: [],
      sources: [],
      inherent_risks: [],
      residual_risks: [],
      standards: [],
    },
  },
  getters: {
    getItems(state) {
      return state.items
    },
    getCurrent(state) {
      return state.current
    },
    getCatalogs(state) {
      return state.catalogs
    },
    getScopeTypes(state) {
      return state.catalogs.scope_types
    },
    getRequestTypes(state) {
      return state.catalogs.request_types
    },
    getExceptionTypes(state) {
      return state.catalogs.exception_types
    },
    getStatuses(state) {
      return state.catalogs.statuses
    },
    getSources(state) {
      return state.catalogs.sources
    },
    getInherentRisks(state) {
      return state.catalogs.inherent_risks
    },
    getResidualRisks(state) {
      return state.catalogs.residual_risks
    },
    getStandards(state) {
      return state.catalogs.standards
    },
  },
  mutations: {
    setData(state, value) {
      state.items = value
    },
    setCurrent(state, value) {
      state.current = value
    },
    setCatalogs(state, payload) {
      const keys = ['scope_types', 'request_types', 'exception_types', 'statuses', 'sources', 'inherent_risks', 'residual_risks', 'standards']
      keys.forEach((key) => {
        if (Array.isArray(payload[key])) {
          state.catalogs[key] = payload[key]
        }
      })
    },
    setStandards(state, value) {
      state.catalogs.standards = Array.isArray(value) ? value : []
    },
    clearCurrent(state) {
      state.current = null
    },
    clear(state) {
      state.items = []
      state.current = null
    },
  },
  actions: {
    async getAll({ commit }, params = {}) {
      const { data } = await axios.get(BASE_PATH, { params })
      if (!data) return { data: [], meta: {}, links: {} }
      commit('setData', data.data || [])
      return data
    },
    async get({ commit }, id) {
      try {
        const { data } = await axios.get(`${BASE_PATH}/${id}`)
        const item = extractPolicyExceptionFromResponse(data)
        commit('setCurrent', item)
        return item
      } catch (_) {
        commit('setCurrent', null)
        return null
      }
    },
    async add(_, payload) {
      const { data } = await axios.post(BASE_PATH, payload)
      return data
    },
    async patch({ commit }, { id, payload }) {
      const { data } = await axios.post(`${BASE_PATH}/${id}`, payload)
      if (data.data) commit('setCurrent', data.data)
      return data
    },
    async updateStatus({ commit }, { id, payload }) {
      const { data } = await axios.post(`${BASE_PATH}/${id}/update_status`, payload)
      const item = extractPolicyExceptionFromResponse(data)
      if (item) commit('setCurrent', item)
      return data
    },
    async fetchCatalogs({ commit }) {
      try {
        const { data } = await axios.get(`${BASE_PATH}/catalogs`)
        const payload = (data && data.data) ? data.data : (data || {})
        commit('setCatalogs', payload)
        return payload
      } catch (_) {
        const fallback = {}
        commit('setCatalogs', fallback)
        return fallback
      }
    },
    async fetchStandards({ commit }) {
      try {
        const { data } = await axios.get(`${BASE_PATH}/getStandards`)
        const list = (data && data.data) ? data.data : (data || [])
        const standards = Array.isArray(list) ? list : []
        commit('setStandards', standards)
        return standards
      } catch (_) {
        commit('setStandards', [])
        return []
      }
    },
    async fetchReviewers() {
      try {
        const { data } = await axios.get(`${BASE_PATH}/getReviewers`)
        const list = (data && data.data) ? data.data : (data || [])
        if (!Array.isArray(list)) return []
        return list.map((u) => ({ value: u.id, label: buildUserOptionLabel(u) }))
      } catch (_) {
        return []
      }
    },
    async fetchReassignable({ dispatch, rootGetters }) {
      try {
        const { data } = await axios.get(`${BASE_PATH}/getReassignable`)
        const list = (data && data.data) ? data.data : (data || [])
        if (Array.isArray(list) && list.length > 0) {
          return list.map((u) => ({ value: u.id, label: buildUserOptionLabel(u) }))
        }
      } catch (_) {
      }
      await dispatch('user/getAll', null, { root: true })
      const users = rootGetters['user/getSelectedItems'] || []
      return users.map((u) => ({ value: u.id, label: buildUserOptionLabel(u) }))
    },
    clear({ commit }) {
      commit('clear')
    },
    async addWithFormData(_, { formData: fd }) {
      const { data } = await axios.post(BASE_PATH, fd)
      return data
    },
    async patchWithFormData({ commit }, { id, formData: fd }) {
      const { data } = await axios.post(`${BASE_PATH}/${id}`, fd)
      if (data.data) commit('setCurrent', data.data)
      return data
    },
  },
}
